/*
 * Unveiling Poppy — Flower Finder widget.
 *
 * Vanilla JS, no framework. Powers two surfaces from one script: a site-wide
 * overlay launched from the header nav (mirrors Malosi's #MaiOverlay) and the
 * inline /pages/find-your-flowers page. Talks to POST /api/finder (Vercel);
 * contract in docs/technical/api.md.
 *
 * Trust boundary: the API `message` is model output. It is HTML-escaped BEFORE the
 * [[delivery]] / [[weddings]] / [[care]] tokens are expanded into controlled links.
 * Model text never reaches innerHTML unescaped.
 */
(function () {
  "use strict";

  var CONFIG = readConfig();
  // Same mark the Liquid templates render inline, repeated here because the follow-up
  // field is built in JS and has to match the main search box exactly. The explicit
  // width/height matter: the widget stylesheet loads at body level, after <main>, so
  // on the page a sparkle with only a viewBox would paint at the SVG default size until
  // the late CSS clamps it — a large flash on load. The attributes size it regardless.
  var SPARKLE_SVG =
    '<svg class="upff-input-sparkle" width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M9 2L10.3 7.7L16 9L10.3 10.3L9 16L7.7 10.3L2 9L7.7 7.7L9 2Z" fill="currentColor"/>' +
    '<path d="M18 8L18.87 11.13L22 12L18.87 12.87L18 16L17.13 12.87L14 12L17.13 11.13L18 8Z" fill="currentColor" opacity="0.7"/>' +
    '<path d="M15 17L15.62 19.38L18 20L15.62 20.62L15 23L14.38 20.62L12 20L14.38 19.38L15 17Z" fill="currentColor" opacity="0.5"/></svg>';
  var CATALOGUE = null; // Map<handle, product> once loaded
  var CATALOGUE_LOADING = false;
  var PENDING = []; // [{ query, output }] queued while the catalogue loads

  // ---- finder attribution (Tier 1) -------------------------------------------
  // Every add-to-cart the finder drives is tagged so the purchase is attributable
  // in the Shopify order — which query surfaced it, and at what rank — with no
  // backend: the data rides as hidden line-item properties (underscore-prefixed, so
  // Shopify stores them on the order but never shows them at checkout). Two paths:
  // the quick-add (single-price cards) writes them straight to /cart/add.js; the
  // link-through (multi-price cards → product page) carries them in the URL and this
  // script injects them into the product form there. See docs backlog "Tier 1".
  var FINDER_TAG = "v1";
  var MAX_ATTR_QUERY = 120;

  function finderProps(query, rank) {
    var p = { _finder: FINDER_TAG };
    if (query) p._finder_query = String(query).slice(0, MAX_ATTR_QUERY);
    if (rank) p._finder_rank = String(rank);
    var sid = finderSessionId();
    if (sid) p._finder_session = sid;
    return p;
  }

  // Session identity for the engagement log (ADR-0016). Prefers Shopify's own
  // first-party session cookie so our events line up with the session the order
  // analytics see; falls back to a self-minted per-tab id so engagement is still
  // countable when the cookie is absent (consent modes, some in-app browsers).
  function readCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function finderSessionId() {
    var sid = readCookie("_shopify_s");
    if (sid) return sid;
    try {
      var own = sessionStorage.getItem("upff_sid");
      if (!own) {
        own = "upff-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        sessionStorage.setItem("upff_sid", own);
      }
      return own;
    } catch (e) {
      return null;
    }
  }

  function finderVisitorId() {
    return readCookie("_shopify_y");
  }

  // Session flags for the nudge (Variation B) and for source attribution. All
  // best-effort: storage can be unavailable (private browsing) and nothing here
  // may ever break the finder itself.
  function sessionFlag(key, set) {
    try {
      if (set) { sessionStorage.setItem(key, "1"); return true; }
      return sessionStorage.getItem(key) === "1";
    } catch (e) { return false; }
  }

  function markEngaged() { sessionFlag("upff_engaged", true); }

  // Where this session's finder visit came from (e.g. the collection-page nudge).
  // Captured once per session from ?src=, sent with every event so Variation B's
  // contribution is measurable in the engagement log.
  function finderSource() {
    try {
      var fromUrl = new URLSearchParams(location.search).get("src");
      if (fromUrl && /^[a-z][a-z0-9-]{0,19}$/.test(fromUrl)) {
        sessionStorage.setItem("upff_src", fromUrl);
      }
      return sessionStorage.getItem("upff_src") || undefined;
    } catch (e) { return undefined; }
  }

  var LOADING_LINES = shuffle([
    "Checking the cool room",
    "Flicking through the order book",
    "Asking the florists",
    "Choosing the freshest stems",
    "Wrapping a few ideas",
  ]);

  // ---- config ----------------------------------------------------------------

  function readConfig() {
    var defaults = {
      apiUrl: "https://up-flower-finder.vercel.app/api/finder",
      moneyFormat: "${{amount}}",
      links: {
        delivery: "/policies/shipping-policy",
        weddings: "/pages/wedding-florist-brisbane",
        care: "/pages/flower-care",
      },
      chips: [],
      placeholders: [],
      finderUrl: "/pages/find-your-flowers",
      nudge: { enabled: false, delaySeconds: 30, collections: [] },
    };
    var el = document.getElementById("upff-config");
    if (!el) return defaults;
    try {
      var p = JSON.parse(el.textContent);
      return {
        apiUrl: p.apiUrl || defaults.apiUrl,
        moneyFormat: p.moneyFormat || defaults.moneyFormat,
        links: {
          delivery: (p.links && p.links.delivery) || defaults.links.delivery,
          weddings: (p.links && p.links.weddings) || defaults.links.weddings,
          care: (p.links && p.links.care) || defaults.links.care,
        },
        chips: Array.isArray(p.chips) ? p.chips : defaults.chips,
        placeholders: Array.isArray(p.placeholders) ? p.placeholders : defaults.placeholders,
        finderUrl: p.finderUrl || "/pages/find-your-flowers",
        nudge: p.nudge && typeof p.nudge === "object"
          ? {
              enabled: p.nudge.enabled === true,
              delaySeconds: typeof p.nudge.delaySeconds === "number" ? p.nudge.delaySeconds : 30,
              collections: Array.isArray(p.nudge.collections) ? p.nudge.collections : [],
            }
          : { enabled: false, delaySeconds: 30, collections: [] },
      };
    } catch {
      return defaults;
    }
  }

  // ---- escaping --------------------------------------------------------------

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Expand the three allowed tokens into links AFTER the surrounding text is escaped,
  // so the only HTML that ever reaches innerHTML is what we control here.
  function renderMessage(message) {
    var html = esc(message);
    html = html.replace(/\[\[delivery\]\]/g, link(CONFIG.links.delivery, "delivery"));
    html = html.replace(/\[\[weddings\]\]/g, link(CONFIG.links.weddings, "wedding enquiry"));
    html = html.replace(/\[\[care\]\]/g, link(CONFIG.links.care, "flower care"));
    return html.replace(/\n/g, "<br>");
  }

  function link(href, label) {
    return '<a class="upff-link" href="' + esc(href) + '">' + esc(label) + "</a>";
  }

  // ---- money -----------------------------------------------------------------

  function formatMoney(cents) {
    var format = CONFIG.moneyFormat;
    if (/\{\{\s*amount_no_decimals\s*\}\}/.test(format)) {
      return format.replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString());
    }
    return format.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  }

  // ---- catalogue -------------------------------------------------------------

  function loadCatalogue() {
    if (CATALOGUE || CATALOGUE_LOADING) return;
    CATALOGUE_LOADING = true;
    fetchPage(1, new Map());
  }

  function fetchPage(page, map) {
    fetch("/collections/all/products.json?limit=250&page=" + page)
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var products = (data && data.products) || [];
        products.forEach(function (p) {
          // products.json prices are decimal-dollar STRINGS ("100.00"), unlike the
          // /products/<handle>.js Ajax API which returns integer cents. Normalise
          // to cents at ingest so formatMoney's contract holds (fleet money rule).
          (p.variants || []).forEach(function (v) {
            v.price = Math.round(parseFloat(v.price) * 100);
          });
          map.set(p.handle, p);
        });
        if (products.length === 250 && page < 8) {
          fetchPage(page + 1, map);
        } else {
          CATALOGUE = map;
          CATALOGUE_LOADING = false;
          var queued = PENDING.slice();
          PENDING = [];
          queued.forEach(function (item) {
            runQuery(item.query, item.output);
          });
        }
      })
      .catch(function () {
        CATALOGUE_LOADING = false;
        // A query submitted before the catalogue finished loading is parked in PENDING
        // and replayed once it arrives. If the load FAILS, those queries used to be
        // dropped silently, leaving the customer on "Checking the cool room…" for ever
        // with no error and no way forward. Drain the queue and say so instead.
        var stranded = PENDING.slice();
        PENDING = [];
        stranded.forEach(function (item) {
          renderError(
            item.output,
            "Sorry — we couldn't load our flowers just now. Please try again shortly.",
          );
        });
      });
  }

  // ---- query -----------------------------------------------------------------

  function runQuery(query, output) {
    query = (query || "").trim();
    if (!query || !output) return;

    if (!CATALOGUE) {
      PENDING.push({ query: query, output: output });
      loadCatalogue();
      showLoading(output, query);
      return;
    }

    markEngaged();
    showLoading(output, query);
    var started = Date.now();

    fetch(CONFIG.apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: query,
        session: finderSessionId() || undefined,
        visitor: finderVisitorId() || undefined,
        source: finderSource(),
      }),
    })
      .then(function (r) {
        return r.json().then(function (body) {
          return { ok: r.ok, body: body };
        });
      })
      .then(function (res) {
        var seconds = ((Date.now() - started) / 1000).toFixed(1);
        if (res.body && res.body.error) {
          renderError(output, res.body.error);
          console.log("[Flower Finder] Error after " + seconds + "s");
          return;
        }
        renderResults(output, query, res.body);
        console.log(
          "[Flower Finder] Response: " + seconds + "s, " + ((res.body.products || []).length) + " products"
        );
      })
      .catch(function () {
        renderError(output, "Sorry — our flower finder is having a moment. Please try again shortly.");
      });
  }

  // ---- rendering -------------------------------------------------------------

  // Once there is an answer the original search box and the chip row are hidden, and
  // the follow-up field rendered under the message takes their place. That is what
  // gives the results page a single, obvious input instead of a stale box still
  // holding the last query with the chips sitting under it.
  function setAnswering(output, on) {
    var surface = output.closest ? output.closest(".upff-surface") : null;
    if (surface) surface.classList.toggle("upff-has-answer", !!on);
  }

  function showLoading(output, query) {
    var line = LOADING_LINES[Math.floor(Math.random() * LOADING_LINES.length)];
    setAnswering(output, true);
    output.innerHTML =
      '<div class="upff-echo">You asked: ' + esc(query) + "</div>" +
      '<div class="upff-loading"><span class="upff-dot"></span>' + esc(line) + "…</div>";
  }

  function renderError(output, message) {
    output.innerHTML = '<div class="upff-error">' + esc(message) + "</div>";
  }

  function renderResults(output, query, body) {
    var parts = [];
    parts.push('<div class="upff-echo">You asked: ' + esc(query) + "</div>");
    parts.push('<div class="upff-message">' + renderMessage(body.message) + "</div>");

    var products = (body.products || [])
      .map(function (p) {
        return CATALOGUE.get(p.handle) ? { api: p, live: CATALOGUE.get(p.handle) } : null;
      })
      .filter(Boolean);

    // Refine sits directly under the answer, above the results, as Malosi's does: a
    // customer can re-ask without scrolling past everything. Measured before the move,
    // the follow-up input sat 823px below the top of the results grid on desktop.
    parts.push(followUpMarkup());

    if (products.length) {
      parts.push('<div class="grid grid--uniform upff-grid">');
      products.forEach(function (item, i) {
        parts.push(productCard(item.live, item.api, i === 0, query, i + 1));
      });
      parts.push("</div>");
    }

    // The API still returns add-ons, and the server still SELECTS them — that selection
    // keeps the prompt's cache prefix stable (ADR-0013), so it stays. They are simply
    // no longer rendered: the "Goes well with" section was dropped 2026-07-24 (Gene).
    output.innerHTML = parts.join("");
    // The query drove every card here; wireAddToCart reads it back for attribution.
    output.setAttribute("data-finder-query", String(query).slice(0, MAX_ATTR_QUERY));
    wireFollowUp(output);
    wireAddToCart(output);
  }

  function pickVariant(live, sizeHint) {
    var variants = live.variants || [];
    if (sizeHint) {
      var want = String(sizeHint).toLowerCase().trim();
      for (var i = 0; i < variants.length; i++) {
        var opts = [variants[i].option1, variants[i].option2, variants[i].option3];
        for (var j = 0; j < opts.length; j++) {
          if (opts[j] && String(opts[j]).toLowerCase().trim() === want) return variants[i];
        }
      }
    }
    for (var k = 0; k < variants.length; k++) {
      if (variants[k].available) return variants[k];
    }
    return variants[0] || null;
  }

  function imageUrl(live) {
    if (live.images && live.images.length) {
      // 600 rather than 400: the square mask crops to the card's full width, so a
      // ~260px card on a 2x handset was upscaling a 400px source.
      return live.images[0].src.replace(/(\.[a-z]+)(\?.*)?$/i, "_600x$1");
    }
    return "";
  }

  // Cards are Impulse's own grid-product markup rather than ours, so finder results
  // render with the store's card styling, typography, price and sold-out tag — and
  // match a collection page exactly. Built against THIS theme (Impulse 7.5.1), not
  // Malosi's 7.4.0: 7.5.1 emits `grid__image-ratio grid__image-ratio--square`, which
  // absolutely positions the image at object-fit:cover natively, where 7.4.0 needed a
  // hand-written `image-wrap` with an inline padding-bottom. That square mask is what
  // makes every card's image the same height — the ragged 184/197px (mobile) and
  // 291/311px (desktop) images came from our own width:100%/height:auto rule.
  //
  // `.upff-card` rides along as a behavioural hook only: the widget's own JS and the
  // end-to-end runner locate cards through it. It carries no styling.
  // Collection tiles print "from $80.00" whenever a product's variants differ in
  // price, and the finder's cards now match them. That makes the quick-add unsafe on
  // those products: the card would advertise $80 while the button carted a $150
  // GRANDE. So a multi-price product links through to its own page — which is exactly
  // what a collection tile does — and the quick-add is kept only where there is a
  // single price and nothing to mislead about.
  function priceLabel(live, variant) {
    var prices = (live.variants || [])
      .map(function (v) { return v.price; })
      .filter(function (p) { return typeof p === "number" && !isNaN(p); });
    if (!prices.length) return { text: variant ? formatMoney(variant.price) : "", varies: false };
    var min = Math.min.apply(null, prices);
    var max = Math.max.apply(null, prices);
    if (min === max) return { text: formatMoney(min), varies: false };
    return { text: "from " + formatMoney(min), varies: true };
  }

  function cardShell(live, opts) {
    var variant = pickVariant(live, opts.size);
    var soldOut = !variant || !variant.available;
    var img = imageUrl(live);
    var priceInfo = priceLabel(live, variant);
    var price = priceInfo.text;
    var url = "/products/" + esc(live.handle);
    // Every anchor on the card carries the finder context, not just the size-picker
    // arrow. A product page reached without ?_fq= stamps no line-item properties, so
    // the purchase it leads to is invisible to Tier 1 attribution — and the image and
    // the title are the links customers actually click. Carrying it on the arrow alone
    // is why six days of finder use produced zero attributed orders while the widget
    // was demonstrably working: not "nobody bought", but "we could not tell".
    var linkUrl = url;
    if (opts.query) {
      linkUrl +=
        "?_fq=" + encodeURIComponent(String(opts.query).slice(0, MAX_ATTR_QUERY)) +
        "&_fr=" + encodeURIComponent(opts.rank || "");
      var linkSid = finderSessionId();
      if (linkSid) linkUrl += "&_fs=" + encodeURIComponent(linkSid);
    }

    var html =
      '<div class="grid__item grid-product small--one-half medium-up--one-quarter ' +
      opts.hook +
      (opts.isBest ? " upff-card--best" : "") +
      '">';
    html += '<div class="grid-product__content">';
    // Sits on the card's top edge, outside the meta. Inside the meta it added a row to
    // the best card only, pushing its title below its neighbours' — that was the
    // misaligned column, not the typography.
    if (opts.isBest) html += '<div class="upff-best-label">Best match</div>';
    if (soldOut) {
      html += '<div class="grid-product__tag grid-product__tag--sold-out">Sold out</div>';
    }
    html += '<div class="grid__item-image-wrapper">';
    // .upff-media exists solely to give the quick-add a positioning context that spans
    // the IMAGE only. Anchoring it to .grid__item-image-wrapper instead put it at the
    // bottom of the whole card, over the title — it clipped "SUNFLOWER MARKET BUN…".
    html += '<div class="upff-media">';
    html += '<a class="grid-product__link" href="' + esc(linkUrl) + '">';
    html += '<div class="grid-product__image-mask">';
    html += '<div class="grid__image-ratio grid__image-ratio--square">';
    if (img) {
      html += '<img src="' + esc(img) + '" alt="' + esc(live.title) + '" loading="lazy">';
    }
    html += "</div></div></a>";
    // The control sits outside the anchor: a button nested inside a link is invalid
    // and swallows the click on some browsers. Where the price varies it becomes a
    // link to the product page rather than a quick-add, so the price on the card is
    // always the price the customer would be charged.
    if (!soldOut && priceInfo.varies) {
      // Link-through carries the finder context in the URL; the product page reads it
      // and injects it as line-item properties on whatever variant the customer adds.
      html +=
        '<a class="upff-quick-add upff-choose" href="' +
        esc(linkUrl) +
        '" aria-label="Choose a size for ' +
        esc(live.title) +
        '">&rarr;</a>';
    } else if (!soldOut) {
      html +=
        '<button class="upff-quick-add upff-add" data-variant="' +
        esc(variant.id) +
        '" data-finder-rank="' + esc(opts.rank || "") +
        '" aria-label="Add ' +
        esc(live.title) +
        ' to cart">+</button>';
    }
    html += "</div>";
    // Mirrors a collection tile: centred title, the catalogue's own subtitle, then the
    // price. --body rather than --heading is the variant the collection grid uses.
    html += '<a class="grid-product__meta" href="' + esc(linkUrl) + '">';
    html += '<div class="grid-product__title grid-product__title--body">' + esc(live.title) + "</div>";
    if (opts.subtitle) {
      html += '<div class="upff-card-subtitle">' + esc(opts.subtitle) + "</div>";
    }
    html += '<div class="grid-product__price">' + esc(price) + "</div>";
    html += "</a>";
    html += "</div></div></div>";
    return html;
  }

  function productCard(live, api, isBest, query, rank) {
    return cardShell(live, {
      hook: "upff-card",
      size: api.size,
      isBest: isBest,
      subtitle: api.subtitle,
      query: query,
      rank: rank,
    });
  }

  // Mirrors the main search box exactly — same hairline field, sparkle and square
  // submit — because after a search this IS the search box: the original one and the
  // chips are hidden, so the customer sees one input that has moved under the answer.
  // START OVER sits on its own row beneath it rather than inline, which is what makes
  // the row read as a field rather than as three crammed controls.
  function followUpMarkup() {
    return (
      '<div class="upff-followup">' +
      '<div class="upff-input-line">' +
      SPARKLE_SVG +
      '<input type="text" class="upff-followup-input" placeholder="Ask a follow-up…" aria-label="Ask a follow-up">' +
      '<button class="upff-submit upff-followup-go" aria-label="Search again">' +
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
      "</button>" +
      "</div>" +
      '<button class="upff-reset">Start over</button>' +
      "</div>"
    );
  }

  // ---- cart ------------------------------------------------------------------

  function wireAddToCart(output) {
    var query = output.getAttribute("data-finder-query") || "";
    var buttons = output.querySelectorAll(".upff-add");
    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener("click", function () {
        var id = btn.getAttribute("data-variant");
        if (!id) return;
        btn.disabled = true;
        var original = btn.textContent;
        btn.textContent = "Adding…";
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: id,
            quantity: 1,
            properties: finderProps(query, btn.getAttribute("data-finder-rank")),
          }),
        })
          .then(function (r) {
            if (!r.ok) throw new Error("add failed");
            return r.json();
          })
          .then(function () {
            btn.textContent = "Added ✓";
            document.dispatchEvent(new CustomEvent("upff:added"));
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = original;
          });
      });
    });
  }

  // ---- follow-up + reset -----------------------------------------------------

  function wireFollowUp(output) {
    var input = output.querySelector(".upff-followup-input");
    var go = output.querySelector(".upff-followup-go");
    var reset = output.querySelector(".upff-reset");
    if (go && input) {
      go.addEventListener("click", function () {
        runQuery(input.value, output);
      });
      input.addEventListener("keydown", function (e) {
        if (e.key === "Enter") runQuery(input.value, output);
      });
    }
    if (reset) {
      reset.addEventListener("click", function () {
        output.innerHTML = "";
        // Start over restores the original box and the chips, and clears the stale
        // query out of the field so the customer starts from a genuinely blank slate.
        setAnswering(output, false);
        var surface = output.closest ? output.closest(".upff-surface") : null;
        var main = surface && surface.querySelector(".upff-input");
        if (main) {
          main.value = "";
          main.focus();
        }
      });
    }
  }

  // ---- chips + typewriter ----------------------------------------------------

  function renderChips(container, input, output, isOverlay) {
    if (!CONFIG.chips.length) return;
    container.innerHTML =
      '<span class="upff-popular-label">Popular:</span>' +
      CONFIG.chips
        .map(function (c) {
          return '<button type="button" class="upff-chip">' + esc(c) + "</button>";
        })
        .join("");
    Array.prototype.forEach.call(container.querySelectorAll(".upff-chip"), function (chip) {
      chip.addEventListener("click", function () {
        var q = chip.textContent;
        if (isOverlay) return handOffToPage(q);
        if (input) input.value = q;
        runQuery(q, output);
      });
    });
  }

  // A chip or a submit in the overlay band opens the dedicated page with the query
  // already applied, rather than rendering results in the band.
  function handOffToPage(query) {
    query = (query || "").trim();
    if (!query) return;
    markEngaged();
    var base = CONFIG.finderUrl || "/pages/find-your-flowers";
    location.href = base + "?q=" + encodeURIComponent(query);
  }

  function startTypewriter(input) {
    var examples = CONFIG.placeholders;
    if (!examples.length) return;
    var idx = 0, char = 0, deleting = false;
    function tick() {
      if (document.activeElement === input || (input.value && input.value.length)) {
        // Snap to the complete phrase before idling. The overlay auto-focuses its
        // input on open, so pausing without this leaves a half-typed placeholder
        // frozen on screen for as long as the field holds focus.
        input.setAttribute("placeholder", examples[idx]);
        char = examples[idx].length;
        deleting = false;
        setTimeout(tick, 1200);
        return;
      }
      var full = examples[idx];
      if (!deleting) {
        char++;
        input.setAttribute("placeholder", full.slice(0, char));
        if (char >= full.length) {
          deleting = true;
          setTimeout(tick, 2600);
          return;
        }
        setTimeout(tick, 55);
      } else {
        char--;
        input.setAttribute("placeholder", full.slice(0, char));
        if (char <= 0) {
          deleting = false;
          idx = (idx + 1) % examples.length;
          setTimeout(tick, 350);
          return;
        }
        setTimeout(tick, 30);
      }
    }
    tick();
  }

  // ---- surfaces + overlay ----------------------------------------------------

  function initSurface(root, isOverlay) {
    var input = root.querySelector(".upff-input");
    var output = root.querySelector(".upff-output");
    var chips = root.querySelector(".upff-chips");
    var submit = root.querySelector(".upff-submit");
    if (!output) return;
    if (input) startTypewriter(input);
    if (chips) renderChips(chips, input, output, isOverlay);
    function go() {
      var q = input ? input.value : "";
      // The overlay is a launcher, not a results surface. Results belong on the
      // dedicated page, which has the store's full container width and reads far
      // better than a 900px band — so submitting from the band hands off to
      // /pages/find-your-flowers?q=…, which already auto-runs the query on load.
      if (isOverlay) return handOffToPage(q);
      runQuery(q, output);
    }
    if (submit) submit.addEventListener("click", go);
    if (input) {
      input.addEventListener("keydown", function (e) {
        if (e.key === "Enter") go();
      });
    }
    return { input: input, output: output, root: root };
  }

  function initOverlay() {
    var overlay = document.getElementById("UpffOverlay");
    if (!overlay) return null;
    var toggles = document.querySelectorAll("[data-upff-toggle]");
    var closes = overlay.querySelectorAll("[data-upff-close]");
    var lastFocused = null;

    function focusables() {
      return Array.prototype.filter.call(
        overlay.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'),
        function (el) {
          return el.offsetParent !== null;
        }
      );
    }

    function setExpanded(v) {
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute("aria-expanded", v ? "true" : "false");
      });
    }
    // The band sits directly under the header, like Malosi's. Ours renders at body
    // level rather than inside <header>, so the offset is measured rather than
    // inherited from position:absolute — this also survives the announcement bar
    // and the shorter mobile header.
    function positionBand() {
      var header = document.getElementById("SiteHeader") || document.querySelector(".site-header");
      var top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      overlay.style.setProperty("--upff-top", top + "px");
    }

    function open() {
      lastFocused = document.activeElement;
      positionBand();
      overlay.classList.add("is-open");
      overlay.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("upff-no-scroll");
      setExpanded(true);
      // On narrow screens the overlay is full-height, so auto-focusing the input
      // raises the keyboard over the chips before the customer has read them.
      // Focus the dialog instead — the focus trap still has somewhere to start.
      var narrow = window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
      var target = narrow
        ? overlay.querySelector(".upff-overlay__panel")
        : overlay.querySelector(".upff-input");
      if (target) setTimeout(function () { target.focus(); }, 60);
    }
    function close() {
      overlay.classList.remove("is-open");
      overlay.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("upff-no-scroll");
      setExpanded(false);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }
    function toggle() {
      if (overlay.classList.contains("is-open")) {
        close();
      } else {
        open();
      }
    }
    window.addEventListener("resize", function () {
      if (overlay.classList.contains("is-open")) positionBand();
    });
    Array.prototype.forEach.call(toggles, function (t) {
      t.addEventListener("click", function (e) {
        e.preventDefault();
        toggle();
      });
    });
    Array.prototype.forEach.call(closes, function (c) {
      c.addEventListener("click", close);
    });
    document.addEventListener("keydown", function (e) {
      if (!overlay.classList.contains("is-open")) return;
      if (e.key === "Escape") {
        close();
        return;
      }
      if (e.key === "Tab") {
        var items = focusables();
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
    return { el: overlay, open: open, close: close };
  }

  // ---- boot ------------------------------------------------------------------

  function shuffle(arr) {
    var a = arr.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i];
      a[i] = a[j];
      a[j] = t;
    }
    return a;
  }

  // On a product page reached from a finder link-through (?_fq=…), stamp the finder
  // context onto the product's add-to-cart form(s) as hidden line-item properties, so
  // the eventual purchase — whatever size/colour the customer picks — carries the
  // attribution. Then strip the params so the URL stays clean and is not re-shared.
  // Defensive: does nothing if no param or no product form, and never throws.
  function applyFinderAttribution() {
    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { return; }
    var q = params.get("_fq");
    if (!q) return;
    var rank = params.get("_fr") || "";
    var sid = params.get("_fs") || "";
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    Array.prototype.forEach.call(forms, function (form) {
      addHiddenInput(form, "properties[_finder]", FINDER_TAG);
      addHiddenInput(form, "properties[_finder_query]", q.slice(0, MAX_ATTR_QUERY));
      if (rank) addHiddenInput(form, "properties[_finder_rank]", rank);
      if (sid) addHiddenInput(form, "properties[_finder_session]", sid);
    });
    if (window.history && history.replaceState) {
      params.delete("_fq");
      params.delete("_fr");
      params.delete("_fs");
      var qs = params.toString();
      history.replaceState({}, "", location.pathname + (qs ? "?" + qs : "") + location.hash);
    }
  }

  function addHiddenInput(form, name, value) {
    var existing = form.querySelector('input[name="' + name + '"][data-upff]');
    if (existing) { existing.value = value; return; }
    var input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    input.setAttribute("data-upff", "1");
    form.appendChild(input);
  }

  // ---- collection-page nudge (Variation B) -----------------------------------
  // A dismissible prompt after ~30s on a flower collection, once per session, and
  // never for a session that has already used the finder. Timing and the collection
  // allowlist live in the overlay config so they are editable without code.
  // Config now comes from the "Flower Finder Nudge" SECTION on the collection
  // template (every metric an editor setting — the playbook build). The old
  // overlay-config nudge remains as a fallback so live keeps its current nudge
  // until the section is added in the editor; the section takes precedence.
  function nudgeConfig() {
    var el = document.getElementById("upff-nudge-config");
    if (el) {
      try {
        var cfg = JSON.parse(el.textContent);
        cfg.tiles = (cfg.tiles || []).filter(Boolean);
        cfg.fromSection = true;
        return cfg.enabled ? cfg : null;
      } catch (e) { return null; }
    }
    var legacy = CONFIG.nudge;
    if (!legacy.enabled || !legacy.collections.length) return null;
    var match = location.pathname.match(/^\/collections\/([^\/?#]+)/);
    if (!match || legacy.collections.indexOf(match[1]) === -1) return null;
    return { delaySeconds: legacy.delaySeconds, tiles: [], fromSection: false };
  }

  function maybeScheduleNudge() {
    var cfg = nudgeConfig();
    if (!cfg) return;
    if (sessionFlag("upff_nudge_done") || sessionFlag("upff_engaged")) return;
    setTimeout(function () {
      if (sessionFlag("upff_nudge_done") || sessionFlag("upff_engaged")) return;
      var overlay = document.getElementById("UpffOverlay");
      if (overlay && overlay.classList.contains("is-open")) return;
      showNudge(cfg);
    }, Math.max(5, cfg.delaySeconds) * 1000);
  }

  function nudgeTileMarkup(tile, isLead) {
    var img = tile.image;
    if (!img && tile.fallbackHandle && CATALOGUE) {
      var live = CATALOGUE.get(tile.fallbackHandle);
      if (live) img = imageUrl(live);
    }
    var href = CONFIG.finderUrl + "?q=" + encodeURIComponent(tile.query) + "&src=nudge";
    var html = '<a class="upff-nudge-tile' + (isLead ? " is-lead" : "") + '" href="' + esc(href) + '">';
    if (img) html += '<img src="' + esc(img) + '" alt="" loading="lazy">';
    html += "<span>" + esc(tile.label) + "</span></a>";
    return html;
  }

  function showNudge(cfg) {
    sessionFlag("upff_nudge_done", true);
    var el = document.createElement("aside");
    el.className = "upff-nudge" + (cfg.fromSection ? "" : " upff-nudge--legacy");
    el.setAttribute("aria-label", "Flower finder suggestion");
    var html = '<button type="button" class="upff-nudge-close" aria-label="Dismiss">&#10005;</button>';
    if (cfg.fromSection) {
      html += '<p class="upff-nudge-title">' + SPARKLE_SVG + esc(cfg.headline) + "</p>";
      html += '<p class="upff-nudge-body">' + esc(cfg.subline) + "</p>";
      if (cfg.tiles.length) {
        html += '<div class="upff-nudge-tiles">';
        for (var i = 0; i < cfg.tiles.length; i++) html += nudgeTileMarkup(cfg.tiles[i], i === 0);
        html += "</div>";
      }
      html += '<div class="upff-nudge-foot">';
      html += '<a class="upff-nudge-cta" href="' + esc(CONFIG.finderUrl) + '?src=nudge">' + esc(cfg.escapeText) + "</a>";
      if (cfg.proofText) html += '<span class="upff-nudge-proof">' + esc(cfg.proofText).replace("★", '<b>&#9733;</b>') + "</span>";
      html += "</div>";
    } else {
      html +=
        '<p class="upff-nudge-title">' + SPARKLE_SVG + "Buying for someone special?</p>" +
        '<p class="upff-nudge-body">Tell our finder the occasion and it will pick the perfect arrangement for you.</p>' +
        '<a class="upff-nudge-cta upff-nudge-cta--btn" href="' + esc(CONFIG.finderUrl) + '?src=nudge">FIND MY FLOWERS &rarr;</a>';
    }
    el.innerHTML = html;
    document.body.appendChild(el);
    el.querySelector(".upff-nudge-close").addEventListener("click", function () {
      el.remove();
    });
    // Entrance on the next frame so the transition runs.
    requestAnimationFrame(function () { el.classList.add("is-in"); });
  }

  function boot() {
    applyFinderAttribution();
    maybeScheduleNudge();
    var overlay = initOverlay();
    var surfaces = [];
    Array.prototype.forEach.call(document.querySelectorAll(".upff-surface"), function (root) {
      var inOverlay = !!(overlay && overlay.el && overlay.el.contains(root));
      var s = initSurface(root, inOverlay);
      if (s) surfaces.push(s);
    });

    // Warm the catalogue if the finder is present at all.
    if (overlay || surfaces.length) loadCatalogue();

    // ?q= auto-run: prefer an inline page surface; otherwise open the overlay.
    var q = new URLSearchParams(location.search).get("q");
    if (q) {
      var page = null;
      surfaces.forEach(function (s) {
        if (!overlay || s.root !== overlay.el) page = page || s;
      });
      if (page) {
        if (page.input) page.input.value = q;
        runQuery(q, page.output);
      } else if (overlay) {
        var oi = overlay.el.querySelector(".upff-input");
        if (oi) oi.value = q;
        overlay.open();
        runQuery(q, overlay.el.querySelector(".upff-output"));
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
